<template>
    <div class="flex gap-2">
        <template v-if="datePartOrder === DateOrder.YearMonthDay">
            <dropdown-date-input-year
                v-model:selected-year="selectedYear"
                :disabled="disabled"
                :invalid="invalid"
                :show-errors="showErrors"
            />

            <dropdown-date-input-month
                v-model:selected-month="selectedMonth"
                :disabled="disabled"
                :invalid="invalid"
                :show-errors="showErrors"
            />

            <dropdown-date-input-day
                v-model:selected-day="selectedDay"
                :disabled="disabled"
                :invalid="invalid"
                :show-errors="showErrors"
                :day-options="dayOptions"
            />
        </template>
        <template v-else-if="datePartOrder === DateOrder.DayMonthYear">
            <dropdown-date-input-day
                v-model:selected-day="selectedDay"
                :disabled="disabled"
                :invalid="invalid"
                :show-errors="showErrors"
                :day-options="dayOptions"
            />

            <dropdown-date-input-month
                v-model:selected-month="selectedMonth"
                :disabled="disabled"
                :invalid="invalid"
                :show-errors="showErrors"
            />

            <dropdown-date-input-year
                v-model:selected-year="selectedYear"
                :disabled="disabled"
                :invalid="invalid"
                :show-errors="showErrors"
            />
        </template>
        <template v-else>
            <dropdown-date-input-month
                v-model:selected-month="selectedMonth"
                :disabled="disabled"
                :invalid="invalid"
                :show-errors="showErrors"
            />

            <dropdown-date-input-day
                v-model:selected-day="selectedDay"
                :disabled="disabled"
                :invalid="invalid"
                :show-errors="showErrors"
                :day-options="dayOptions"
            />

            <dropdown-date-input-year
                v-model:selected-year="selectedYear"
                :disabled="disabled"
                :invalid="invalid"
                :show-errors="showErrors"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
import { ref, toRef, watch, watchEffect } from 'vue'
import { isLeapYear, isValid, parseISO } from 'date-fns'
import { toTimelessIso } from '~/composables/support/localization'
import DropdownDateInputMonth from '~/components/Input/DropdownDateInputMonth.vue'
import DropdownDateInputDay from '~/components/Input/DropdownDateInputDay.vue'
import DropdownDateInputYear from '~/components/Input/DropdownDateInputYear.vue'
import { DateOrder, useLocalization } from '~/composables/use-localization'

const props = defineProps<{
    date: string
    showErrors: boolean
    disabled?: boolean
    invalid?: boolean
}>()

const emit = defineEmits<{
    (e: 'update:date', payload: string): void
}>()

const selectedMonth = ref<string>(0)
const selectedDay = ref<string>(0)
const selectedYear = ref<string>(0)

const { datePartOrder } = useLocalization()

watch(
    toRef(props, 'date'),
    newValue => {
        // If the new value is resetting to blank, we only want to reset the internal state
        // if we have a completely valid value
        if (newValue === '') {
            if (
                selectedYear.value === 0 ||
                selectedMonth.value === 0 ||
                selectedMonth.value === 0
            ) {
                return
            }

            const newDate = parseISO(
                selectedYear.value +
                    '-' +
                    String(selectedMonth.value).padStart(2, '0') +
                    '-' +
                    String(selectedMonth.value).padStart(2, '0')
            )

            if (isValid(newDate)) {
                selectedMonth.value = 0
                selectedYear.value = 0
                selectedDay.value = 0
            }
        } else {
            const date = parseISO(props.date)
            if (!isValid(date)) {
                throw new Error('Error parsing date.')
            }

            selectedMonth.value = date.getMonth() + 1
            selectedYear.value = date.getFullYear()
            selectedDay.value = date.getDate()
        }
    },
    { immediate: true }
)

// First add 28 days to dayOptions, it will be set by the watchEffect below
const dayOptions = ref<{ name: string; value: number }[]>([])
for (let i = 1; i <= 28; i++) {
    dayOptions.value.push({ name: i.toString(), value: i })
}

// This code runs initially and when selectedMonth or selectedYear are changed
// It updates dayOptions accordingly
watchEffect(() => {
    // Reset the dayOptions options to just 28 days
    if (dayOptions.value.length > 28) {
        while (dayOptions.value.length > 28) {
            dayOptions.value.pop()
        }
    }

    // If no month has been selected, or we select a 31-day month
    // Set dayOptions options to be 31 days
    if (
        // 0 represents no selection here
        selectedMonth.value === 0 ||
        selectedMonth.value === 1 ||
        selectedMonth.value === 3 ||
        selectedMonth.value === 5 ||
        selectedMonth.value === 7 ||
        selectedMonth.value === 8 ||
        selectedMonth.value === 10 ||
        selectedMonth.value === 12
    ) {
        dayOptions.value.push({ name: '29', value: 29 })
        dayOptions.value.push({ name: '30', value: 30 })
        dayOptions.value.push({ name: '31', value: 31 })
        return
    }

    // If the selected month is February
    if (selectedMonth.value === 2) {
        // If the selected year is a leap year return 29 days
        if (
            selectedYear.value === 0 ||
            isLeapYear(new Date(selectedYear.value, 1))
        ) {
            dayOptions.value.push({ name: '29', value: 29 })
        }
        return
    }

    // Else we must have selected a 30-day month
    dayOptions.value.push({ name: '29', value: 29 })
    dayOptions.value.push({ name: '30', value: 30 })
})

// If our selectedDay is not present in dayOptions, unselect
// This covers the case of selecting the 31st, then switching the month to February
watchEffect(() => {
    if (
        !dayOptions.value.some(
            dayOption => dayOption.value === selectedDay.value
        )
    ) {
        selectedDay.value = 0
    }
})

watch(
    [selectedMonth, selectedYear, selectedDay],
    ([newMonth, newYear, newDay]) => {
        if (newMonth === 0 || newYear === 0 || newDay === 0) {
            emit('update:date', '')
            return
        }

        const newDate = parseISO(
            newYear +
                '-' +
                String(newMonth).padStart(2, '0') +
                '-' +
                String(newDay).padStart(2, '0')
        )

        if (isValid(newDate)) {
            emit('update:date', toTimelessIso(newDate))
        } else {
            emit('update:date', '')
        }
    }
)
</script>
